import React from "react";
import ManageAccountsOutlined from "@mui/icons-material/ManageAccountsOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import Button, { ButtonTypes } from "../../components/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  IconContainer,
  TitleRow,
  AccountSection,
  SectionEyebrow,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  ActionList,
  ActionRow,
  ActionInfo,
  ActionRowTitle,
  ActionRowDescription,
  ActionControl,
  DangerCard,
  DangerTitleRow,
} from "./styles";
import { DeletePopUp } from "../../components/PopUp";
import { useAccountManagement } from "./hooks/useAccountManagement";

const STAT_PLACEHOLDER = "—";

export const AccountManagementComponent: React.FC = () => {
  const [deactivate, setDeactivate] = React.useState(false);
  const [deletePop, setDeletePop] = React.useState(false);
  const [revokeSessionsPopUp, setRevokeSessionsPopUp] = React.useState(false);

  const {
    accountStats,
    isRevokingSessions,
    handleExportData,
    handleDeactivate,
    handleDelete,
    handleRevokeAllSessions,
  } = useAccountManagement();

  const confirmRevokeAllSessions = React.useCallback(async () => {
    const revoked = await handleRevokeAllSessions();
    if (revoked) {
      setRevokeSessionsPopUp(false);
    }
  }, [handleRevokeAllSessions]);

  const memberSince = accountStats?.createdAt ?? STAT_PLACEHOLDER;
  const totalTransactions = accountStats
    ? accountStats.totalTransactions.toLocaleString()
    : STAT_PLACEHOLDER;
  const totalAccounts = accountStats
    ? accountStats.totalAccounts.toLocaleString()
    : STAT_PLACEHOLDER;
  const firstTransaction =
    accountStats?.firstTransactionDate ?? STAT_PLACEHOLDER;

  const activeSessions = accountStats?.totalActiveSessions ?? 0;
  const sessionsLabel = activeSessions === 1 ? "session" : "sessions";
  const activeSessionsDescription = `You have ${activeSessions} active ${sessionsLabel}. Sign out everywhere except this device.`;
  const canRevokeSessions = activeSessions > 1;

  return (
    <>
      {deactivate && (
        <DeletePopUp
          isOpen={deactivate}
          setIsOpen={setDeactivate}
          title="Account Deactivation"
          handleOnDelete={handleDeactivate}
          text="Do you really want to deactivate your account?"
        />
      )}
      {deletePop && (
        <DeletePopUp
          isOpen={deletePop}
          setIsOpen={setDeletePop}
          title="Account Deletion"
          handleOnDelete={handleDelete}
          text="Do you really want to delete your account? Account deletion is permanent and cannot be undone. All your investment data, transaction history, and account information will be permanently removed."
        />
      )}
      {revokeSessionsPopUp && (
        <DeletePopUp
          isOpen={revokeSessionsPopUp}
          setIsOpen={setRevokeSessionsPopUp}
          title="Revoke All Sessions"
          handleOnDelete={confirmRevokeAllSessions}
          isLoading={isRevokingSessions}
          text={
            <>
              You currently have <strong>{activeSessions}</strong> active
              session(s). This will log you out from all other devices except
              this one.
              <br />
              <br />
              Are you sure you want to continue?
            </>
          }
        />
      )}
      <Card className="account">
        <CardHeader>
          <TitleRow>
            <IconContainer>
              <ManageAccountsOutlined />
            </IconContainer>
            <CardTitle>Account management</CardTitle>
          </TitleRow>
          <CardDescription>
            View your account information and manage critical account decisions.
          </CardDescription>
        </CardHeader>

        <AccountSection>
          <SectionEyebrow>Account statistics</SectionEyebrow>
          <StatsGrid>
            <StatCard>
              <StatLabel>Member since</StatLabel>
              <StatValue>{memberSince}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Total transactions</StatLabel>
              <StatValue>{totalTransactions}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Total accounts</StatLabel>
              <StatValue>{totalAccounts}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>First transaction</StatLabel>
              <StatValue>{firstTransaction}</StatValue>
            </StatCard>
          </StatsGrid>
        </AccountSection>

        <AccountSection>
          <SectionEyebrow>Account actions</SectionEyebrow>
          <ActionList>
            <ActionRow>
              <ActionInfo>
                <ActionRowTitle>Export data</ActionRowTitle>
                <ActionRowDescription>
                  Download a complete copy of your transactions, investments and
                  preferences.
                </ActionRowDescription>
              </ActionInfo>
              <ActionControl>
                <Button
                  type={ButtonTypes.revert}
                  buttonType="button"
                  onClick={handleExportData}
                >
                  Export data
                </Button>
              </ActionControl>
            </ActionRow>

            <ActionRow>
              <ActionInfo>
                <ActionRowTitle>Active sessions</ActionRowTitle>
                <ActionRowDescription>
                  {activeSessionsDescription}
                </ActionRowDescription>
              </ActionInfo>
              <ActionControl>
                <Button
                  type={ButtonTypes.revert}
                  buttonType="button"
                  onClick={() => setRevokeSessionsPopUp(true)}
                  disabled={!canRevokeSessions}
                  isLoading={isRevokingSessions}
                >
                  {isRevokingSessions ? "Revoking..." : "Revoke all"}
                </Button>
              </ActionControl>
            </ActionRow>

            <ActionRow>
              <ActionInfo>
                <ActionRowTitle>Deactivate account</ActionRowTitle>
                <ActionRowDescription>
                  Temporarily disable your account. Reversible, reactivate any
                  time.
                </ActionRowDescription>
              </ActionInfo>
              <ActionControl>
                <Button
                  type={ButtonTypes.warning}
                  buttonType="button"
                  onClick={() => setDeactivate(true)}
                >
                  Deactivate
                </Button>
              </ActionControl>
            </ActionRow>
          </ActionList>

          <DangerCard>
            <ActionInfo>
              <DangerTitleRow>
                <WarningAmberOutlined />
                <ActionRowTitle>Delete account</ActionRowTitle>
              </DangerTitleRow>
              <ActionRowDescription>
                Permanently delete your account and all data. This cannot be
                undone.
              </ActionRowDescription>
            </ActionInfo>
            <ActionControl>
              <Button
                type={ButtonTypes.dangerOutline}
                buttonType="button"
                onClick={() => setDeletePop(true)}
              >
                Delete account
              </Button>
            </ActionControl>
          </DangerCard>
        </AccountSection>
      </Card>
    </>
  );
};

export const AccountManagement = React.memo(AccountManagementComponent);
